import type { RouteObject } from 'react-router-dom';
import type { AppRoute } from 'src/routes/types';

import { Navigate } from 'react-router-dom';
import { CompanyRequiredGuard } from 'src/organization/guards/CompanyRequiredGuard';
import { joinPath } from 'src/shared/utils/joinPath';

import AuthGuard from '../auth/AuthGuard';
import GuestGuard from '../auth/GuestGuard';
import { PATH_AFTER_LOGIN } from '../config-global';
import { useAdminUser } from '../hooks/useAdminUser';
import CompactLayout from '../layouts/compact';
import DashboardLayout from '../layouts/dashboard';
import {
  ClientDetailsNavigator,
  ClientListNavigator,
  CustomerDetailsNavigator,
  FosterFamilyDetailsNavigator,
  FosterFamilyListNavigator,
  PlacementDetailsNavigator,
  PlacementListNavigator,
  UserDetailsNavigator,
} from '../navigators';
import {
  ACTIVE,
  ALL,
  CHANGE_PASSWORD,
  CLIENTS,
  COMPANY,
  CUSTOMERS,
  END,
  ENDED,
  FOSTER_FAMILIES,
  FULL,
  HOME,
  INACTIVATED,
  INACTIVE,
  LIST,
  NEW,
  PENDING,
  PLACEMENTS,
  PROFILE,
  READY,
  ROOTS_DASHBOARD,
  USERS,
} from './consts';
import {
  ChangePasswordPage,
  ClientListPage,
  ClientNewPage,
  ClientPlacementsPage,
  ClientProfilePage,
  CurrentUserProfilePage,
  CustomerListPage,
  CustomerNewPage,
  CustomerPlacementsPage,
  CustomerProfilePage,
  DashboardPage,
  EndPlacementPage,
  ForgotPasswordPage,
  FosterFamilyListPage,
  FosterFamilyNewPage,
  FosterFamilyPlacementsPage,
  FosterFamilyProfilePage,
  LoginPage,
  Page404,
  PlacementListPage,
  PlacementNewPage,
  PlacementProfilePage,
  SetPasswordPage,
  UserListPage,
  UserNewPage,
  UserProfilePage,
  ValidateShareDocumentPage,
  ValidateShareVaultFilePage,
} from './elements';
import { PATH_DASHBOARD } from './paths';

const dangerouslyFindRoute = (route: RouteObject, path: string) => {
  const parts = path.split('/').filter(Boolean);
  let currentRoute = route;

  for (const part of parts) {
    const foundChild = currentRoute.children?.find(
      (child) => child.path === part
    );

    if (foundChild) {
      currentRoute = foundChild;
    } else {
      return null;
    }
  }

  return currentRoute;
};

type UseLegacyRoutesProps = {
  privateAppRoutes: AppRoute[];
};

const useLegacyRoutes = ({
  privateAppRoutes,
}: UseLegacyRoutesProps): RouteObject[] => {
  const { isAdmin } = useAdminUser();

  const routes = [
    {
      children: [
        { element: <Navigate replace to={PATH_AFTER_LOGIN} />, index: true },
        {
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
          path: 'login',
        },
        {
          element: (
            <GuestGuard>
              <SetPasswordPage />
            </GuestGuard>
          ),
          path: 'set-password',
        },
        {
          element: (
            <GuestGuard>
              <ForgotPasswordPage />
            </GuestGuard>
          ),
          path: 'forgot-password',
        },
      ],
      path: '/',
    },
    {
      children: [
        { element: <Navigate replace to={PATH_AFTER_LOGIN} />, index: true },
        { element: <DashboardPage />, path: HOME },
        { element: <CurrentUserProfilePage />, path: PROFILE },
        {
          children: [
            {
              element: (
                <Navigate replace to={PATH_DASHBOARD.fosterFamily.list} />
              ),
              index: true,
            },
            {
              children: [
                { element: <Navigate replace to={ALL} />, index: true },
                { element: <FosterFamilyListPage />, path: ALL },
                { element: <FosterFamilyListPage />, path: READY },
                { element: <FosterFamilyListPage />, path: PENDING },
                { element: <FosterFamilyListPage />, path: FULL },
                { element: <FosterFamilyListPage />, path: INACTIVATED },
              ],
              element: <FosterFamilyListNavigator />,
              path: LIST,
            },
            { element: <FosterFamilyNewPage />, path: NEW },
            {
              children: [
                { element: <Navigate replace to={PROFILE} />, index: true },
                { element: <FosterFamilyProfilePage />, path: PROFILE },
                { element: <FosterFamilyPlacementsPage />, path: PLACEMENTS },
              ],
              element: <FosterFamilyDetailsNavigator />,
              path: ':id',
            },
          ],
          path: FOSTER_FAMILIES,
        },
        {
          children: [
            {
              element: <Navigate replace to={PATH_DASHBOARD.client.list} />,
              index: true,
            },
            {
              children: [
                { element: <Navigate replace to={ACTIVE} />, index: true },
                { element: <ClientListPage />, path: ALL },
                { element: <ClientListPage />, path: ACTIVE },
                { element: <ClientListPage />, path: INACTIVE },
              ],
              element: <ClientListNavigator />,
              path: LIST,
            },
            { element: <ClientNewPage />, path: NEW },
            {
              children: [
                { element: <Navigate replace to={PROFILE} />, index: true },
                { element: <ClientProfilePage />, path: PROFILE },
                { element: <ClientPlacementsPage />, path: PLACEMENTS },
              ],
              element: <ClientDetailsNavigator />,
              path: ':id',
            },
          ],
          path: CLIENTS,
        },
        {
          children: [
            {
              element: <Navigate replace to={PATH_DASHBOARD.customer.list} />,
              index: true,
            },
            { element: <CustomerListPage />, path: LIST },
            { element: <CustomerNewPage />, path: NEW },
            {
              children: [
                { element: <Navigate replace to={PROFILE} />, index: true },
                { element: <CustomerProfilePage />, path: PROFILE },
                { element: <CustomerPlacementsPage />, path: PLACEMENTS },
              ],
              element: <CustomerDetailsNavigator />,
              path: ':id',
            },
          ],
          path: CUSTOMERS,
        },
        {
          children: [
            {
              element: <Navigate replace to={PATH_DASHBOARD.placement.list} />,
              index: true,
            },
            {
              children: [
                { element: <Navigate replace to={ACTIVE} />, index: true },
                { element: <PlacementListPage />, path: ACTIVE },
                { element: <PlacementListPage />, path: ENDED },
                { element: <PlacementListPage />, path: ALL },
              ],
              element: <PlacementListNavigator />,
              path: LIST,
            },
            { element: <PlacementNewPage />, path: NEW },
            {
              children: [
                { element: <Navigate replace to={PROFILE} />, index: true },
                { element: <PlacementProfilePage />, path: PROFILE },
                { element: <EndPlacementPage />, path: END },
              ],
              element: <PlacementDetailsNavigator />,
              path: ':id',
            },
          ],
          path: PLACEMENTS,
        },
        {
          children: [
            {
              element: <Navigate replace to={PATH_DASHBOARD.user.list} />,
              index: true,
            },
            { element: <UserListPage />, path: LIST },
            {
              element: isAdmin ? (
                <UserNewPage />
              ) : (
                <Navigate replace to={PATH_DASHBOARD.user.list} />
              ),
              path: NEW,
            },
            {
              children: [
                { element: <Navigate replace to={PROFILE} />, index: true },
                { element: <UserProfilePage />, path: PROFILE },
                { element: <ChangePasswordPage />, path: CHANGE_PASSWORD },
              ],
              element: <UserDetailsNavigator />,
              path: ':id',
            },
          ],
          path: USERS,
        },
        {
          children: [
            {
              element: (
                <Navigate replace to={PATH_DASHBOARD.company.documents} />
              ),
              index: true,
            },
          ],
          path: COMPANY,
        },
        // ...privateAppRoutes,
      ],
      element: (
        <AuthGuard>
          <CompanyRequiredGuard>
            <DashboardLayout privateAppRoutes={privateAppRoutes} />
          </CompanyRequiredGuard>
        </AuthGuard>
      ),
      path: `/${ROOTS_DASHBOARD}`,
    },
    {
      children: [
        { element: <Page404 />, path: '404' },
        {
          element: <ValidateShareDocumentPage />,
          path: 'download/document/:id',
        },
        { element: <ValidateShareVaultFilePage />, path: 'download/file/:id' },
      ],
      element: <CompactLayout />,
    },
    { element: <Navigate replace to="/404" />, path: '*' },
  ];

  privateAppRoutes
    .sort((a, b) => {
      return (a.order || 0) - (b.order || 0);
    })
    .forEach((route) => {
      if (route._dangerouslyInjectAsChildForLegacyPath) {
        const match = dangerouslyFindRoute(
          routes[1],
          route._dangerouslyInjectAsChildForLegacyPath
        );
        if (match) {
          match.children?.push({
            ...route,
            path: route.path.replace(
              joinPath(
                `/${ROOTS_DASHBOARD}`,
                route._dangerouslyInjectAsChildForLegacyPath
              ),
              ''
            ),
          });
        }
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      routes[1].children?.push(route as any);
    });

  return routes;
};

export default useLegacyRoutes;
